import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import * as api from './api';
import type { CreateLlmKeyBody, LlmKeyRow, ProviderId, UpdateLlmKeyBody } from './schemas';

const KEYS = {
  catalog: (tid: string) => ['settings', 'llm-catalog', tid] as const,
  list: (tid: string) => ['settings', 'llm-keys', tid] as const,
};

function useActiveTenantId(): string {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  if (!tenantId) throw new Error('useActiveTenantId called without an active tenant');
  return tenantId;
}

export function useLlmCatalog() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: KEYS.catalog(tenantId),
    queryFn: () => api.getCatalog(tenantId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLlmKeys() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: KEYS.list(tenantId),
    queryFn: () => api.listKeys(tenantId),
  });
}

export function useCreateLlmKey() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLlmKeyBody) => api.createKey(tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.list(tenantId) });
    },
  });
}

export function useUpdateLlmKey() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLlmKeyBody }) =>
      api.updateKey(tenantId, id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.list(tenantId) });
    },
  });
}

export function useDeleteLlmKey() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteKey(tenantId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.list(tenantId) });
    },
  });
}

export function useTestLlmKey() {
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: { id: string } | { provider: ProviderId; apiKey: string }) =>
      'id' in input ? api.testStored(tenantId, input.id) : api.testPreSave(tenantId, input),
  });
}

export type { LlmKeyRow };
